import { PROJECTED_TILE_SIZE } from 'src/config';
import type { RectangleLabelPosition } from 'src/schemas/rectangle';
import type { Coords } from 'src/types';

export interface RectangleLabelLayout {
  tile: Coords;
  rotation: number;
  offset: Coords;
}

// Rectangle bounds identify tile centers, while the visible rectangle extends
// another half tile beyond those centers. Clear that footprint first, then
// add half the label height and a small visual gap.
const TILE_CENTER_TO_VISIBLE_EDGE = Math.hypot(
  PROJECTED_TILE_SIZE.width / 2,
  PROJECTED_TILE_SIZE.height / 2
) / 2;
const LABEL_HALF_HEIGHT = 16;
const LABEL_BORDER_GAP = 8;
const LABEL_EDGE_CLEARANCE = TILE_CENTER_TO_VISIBLE_EDGE
  + LABEL_HALF_HEIGHT
  + LABEL_BORDER_GAP;

const getProjectedPosition = (tile: Coords): Coords => {
  return {
    x: PROJECTED_TILE_SIZE.width / 2 * (tile.x - tile.y),
    y: -PROJECTED_TILE_SIZE.height / 2 * (tile.x + tile.y)
  };
};

const getReadableEdgeAngle = (from: Coords, to: Coords): number => {
  const projectedFrom = getProjectedPosition(from);
  const projectedTo = getProjectedPosition(to);
  let angle = Math.atan2(
    projectedTo.y - projectedFrom.y,
    projectedTo.x - projectedFrom.x
  ) * 180 / Math.PI;

  if (angle > 90) angle -= 180;
  if (angle <= -90) angle += 180;

  return angle;
};

const midpoint = (from: Coords, to: Coords): Coords => ({
  x: (from.x + to.x) / 2,
  y: (from.y + to.y) / 2
});

const getOutwardScreenOffset = (edge: Coords, center: Coords): Coords => {
  const projectedEdge = getProjectedPosition(edge);
  const projectedCenter = getProjectedPosition(center);
  const dx = projectedEdge.x - projectedCenter.x;
  const dy = projectedEdge.y - projectedCenter.y;
  const distance = Math.hypot(dx, dy);

  if (distance === 0) return { x: 0, y: 0 };

  return {
    x: dx / distance * LABEL_EDGE_CLEARANCE,
    y: dy / distance * LABEL_EDGE_CLEARANCE
  };
};

export const getRectangleLabelLayout = (
  from: Coords,
  to: Coords,
  position: RectangleLabelPosition = 'CENTER'
): RectangleLabelLayout => {
  const lowX = Math.min(from.x, to.x);
  const highX = Math.max(from.x, to.x);
  const lowY = Math.min(from.y, to.y);
  const highY = Math.max(from.y, to.y);

  const corners = {
    BOTTOM_LEFT: { x: lowX, y: lowY },
    BOTTOM_RIGHT: { x: highX, y: lowY },
    TOP_RIGHT: { x: highX, y: highY },
    TOP_LEFT: { x: lowX, y: highY }
  } satisfies Record<Exclude<RectangleLabelPosition, 'CENTER'>, Coords>;

  const center = {
    x: (lowX + highX) / 2,
    y: (lowY + highY) / 2
  };
  const horizontalAngle = getReadableEdgeAngle(
    corners.TOP_LEFT,
    corners.TOP_RIGHT
  );
  const verticalAngle = getReadableEdgeAngle(
    corners.BOTTOM_LEFT,
    corners.TOP_LEFT
  );

  if (position === 'CENTER') {
    return {
      tile: center,
      rotation: horizontalAngle,
      offset: { x: 0, y: 0 }
    };
  }

  const edges = {
    TOP_LEFT: {
      midpoint: midpoint(corners.TOP_LEFT, corners.TOP_RIGHT),
      rotation: horizontalAngle
    },
    TOP_RIGHT: {
      midpoint: midpoint(corners.TOP_RIGHT, corners.BOTTOM_RIGHT),
      rotation: verticalAngle
    },
    BOTTOM_LEFT: {
      midpoint: midpoint(corners.BOTTOM_LEFT, corners.TOP_LEFT),
      rotation: verticalAngle
    },
    BOTTOM_RIGHT: {
      midpoint: midpoint(corners.BOTTOM_RIGHT, corners.BOTTOM_LEFT),
      rotation: horizontalAngle
    }
  } satisfies Record<Exclude<RectangleLabelPosition, 'CENTER'>, {
    midpoint: Coords;
    rotation: number;
  }>;
  const edge = edges[position];

  return {
    tile: edge.midpoint,
    rotation: edge.rotation,
    offset: getOutwardScreenOffset(edge.midpoint, center)
  };
};
